import React from "react";
import tutorialVideo from "./assets/timesheet_tutorial.mp4";

import {
  Box,
  Typography,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText
} from "@mui/material";

import { NAVY, LABEL_GREY, CARD_BG, OUTLINE } from "./colors";

// Badge numéroté (1,2,3)
function StepBadge({ index }) {
  return (
    <Box
      aria-hidden
      sx={{
        minWidth: 30,
        height: 30,
        borderRadius: "999px",
        bgcolor: NAVY,
        color: "#fff",
        fontWeight: 800,
        fontSize: 15,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 10px rgba(47,78,139,0.22)",
        mr: 1.5,
        mt: "2px",
        flexShrink: 0,
      }}
    >
      {index}
    </Box>
  );
}

// Texte commercial
const descriptionParagraphs = [
  "Cette vidéo de démonstration vous montre comment CleverSched devient votre tableau de bord RH au quotidien.",
  "En quelques minutes, vous découvrez le parcours complet d’un collaborateur et de son administrateur : demande et validation de congés, gestion du télétravail occasionnel ou récurrent, visualisation dans la feuille de temps, puis vue globale en calendrier et tableau de bord.",
  "L’objectif est de vous permettre de présenter une solution intuitive et prête à l’emploi pour centraliser la gestion des absences, du télétravail et des activités, tout en offrant une visibilité claire sur l’organisation des équipes.",
];

// Liste des étapes
const steps = [
  "Ajout d’un congé par le collaborateur: En quelques clics, le collaborateur choisit le type de congé, les dates, la partie de journée et ajoute une description. La durée est calculée automatiquement et la demande est envoyée à l’administrateur.",
  "Validation du congé par l’administrateur: L’administrateur reçoit la demande, l’accepte ou la refuse, et le solde de congés est mis à jour automatiquement. Une fois validé, le congé devient visible dans la feuille de temps du collaborateur.",
  "Affichage automatique dans la feuille de temps: Les congés validés s’affichent directement dans la feuille de temps. L’utilisateur peut activer l’option « Afficher les congés » pour voir les jours concernés, intégrés au calcul du total d’activité du mois.",
  "Demande de télétravail exceptionnel: Le collaborateur peut soumettre une demande de télétravail ponctuelle en sélectionnant les dates et en précisant le motif, avant de l’envoyer à son administrateur pour validation.",
  "Demande de télétravail récurrent: Pour un télétravail récurrent, le collaborateur définit la période, les jours de la semaine et la fréquence, avec la possibilité d’ajouter des exceptions de présence au bureau. La demande est ensuite soumise à validation.",
  "Gestion du télétravail par l’administrateur: L’administrateur centralise toutes les demandes de télétravail dans son espace, les approuve ou les refuse selon les besoins de l’entreprise. Les jours validés apparaissent automatiquement dans le calendrier de la société.",
  "Vue calendrier – Vision globale: Le calendrier offre une vue d’ensemble des collaborateurs, de leurs jours de présence, de congés et de télétravail. L’administrateur visualise en un coup d’œil l’organisation des équipes et peut consulter les soldes restants.",
  "Tableau de bord – Suivi centralisé: Le tableau de bord regroupe les informations clés : collaborateurs actifs, congés et télétravails en attente, CRA à valider. Chaque carte collaborateur indique son statut (en congé, en télétravail ou présent).",
  "Synthèse finale: La vidéo se termine par une synthèse : CleverSched centralise la gestion des congés, du télétravail et des feuilles de temps pour offrir une vision claire, partagée et fiable de l’activité. CleverSched – planifiez intelligemment, travaillez sereinement.",
];

export default function GuidePage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fb",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          borderBottom: `1px solid ${OUTLINE}`,
          bgcolor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            letterSpacing: 0.4,
            fontSize: { xs: 20, md: 24 },
            mb: 0.6,
          }}
        >
          Vidéo de démonstration — CleverSched
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ color: LABEL_GREY, fontSize: { xs: 13.5, md: 14.5 } }}
        >
          Présentation complète de la gestion des congés, du télétravail et des feuilles de temps.
        </Typography>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* VIDEO BLOCK */}
        <Box sx={{ flex: 3.2, minWidth: 0 }}>
          <Box
            sx={{
              width: "100%",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#000",
              boxShadow: "0 10px 30px rgba(0,0,0,0.32)",
            }}
          >
            <video
              controls
              preload="auto"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            >
              <source src={tutorialVideo} type="video/mp4" />
              Votre navigateur ne supporte pas la vidéo HTML5.
            </video>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1.5,
              color: LABEL_GREY,
              textAlign: { xs: "left", md: "center" },
              fontSize: { xs: 12.5, md: 13.5 },
            }}
          >
            Utilisez cette vidéo comme support lors de vos démonstrations.
          </Typography>
        </Box>

        {/* RIGHT DESCRIPTION */}
        <Box
          sx={{
            flex: 2,
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
            maxHeight: { xs: "none", md: "calc(100vh - 150px)" },
            overflowY: { xs: "visible", md: "auto" },
            pr: { xs: 0, md: 1 },
            scrollbarWidth: "thin",
            scrollbarColor: `${NAVY} #e4e8fb`,
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: NAVY, borderRadius: 8 },
            "&::-webkit-scrollbar-track": { backgroundColor: "#e4e8fb" },
          }}
        >
          {/* ABOUT BLOCK */}
          <Box
            sx={{
              mb: 2.5,
              p: 2.2,
              background: CARD_BG,
              borderRadius: 2,
              border: `1px solid ${OUTLINE}`,
              boxShadow: "0 2px 10px rgba(22, 28, 45, 0.10)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: NAVY,
                fontWeight: 800,
                mb: 0.8,
                fontSize: { xs: 13, md: 14 },
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              À propos de cette vidéo
            </Typography>

            {descriptionParagraphs.map((para, i) => (
              <Typography
                key={i}
                sx={{
                  color: "#222",
                  lineHeight: 1.8,
                  fontSize: { xs: 14.5, md: 15.5 },
                  mb: i === descriptionParagraphs.length - 1 ? 0 : 1.1,
                  textAlign: "justify",
                }}
              >
                {para}
              </Typography>
            ))}
          </Box>

          {/* VIDEO PLAN */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                letterSpacing: 0.3,
                fontSize: { xs: 16.5, md: 18 },
              }}
            >
              Plan de la vidéo
            </Typography>
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <List sx={{ pt: 0, mb: 1, flexGrow: 1 }}>
            {steps.map((text, index) => {
              const hasColon = text.includes(":");
              const primary = hasColon ? text.split(":")[0].trim() : text;
              const secondary = hasColon ? text.split(":").slice(1).join(":").trim() : null;

              return (
                <ListItem key={index} sx={{ px: 0, py: 1.1 }}>
                  <StepBadge index={index + 1} />

                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          color: "#222",
                          fontSize: { xs: 14.5, md: 15.5 },
                          fontWeight: 600,
                          mb: 0.25,
                        }}
                      >
                        {primary}
                      </Typography>
                    }
                    secondary={
                      secondary && (
                        <Typography
                          sx={{
                            color: LABEL_GREY,
                            fontSize: { xs: 13.5, md: 14.5 },
                            lineHeight: 1.6,
                          }}
                        >
                          {secondary}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 1.5,
          borderTop: `1px dashed ${OUTLINE}`,
          textAlign: "center",
        bgcolor: "#fff",

        }}
      >
        <Typography sx={{ color: LABEL_GREY, fontSize: 13 }}>
          CleverSched – solution de gestion RH. Contactez notre équipe commerciale pour en savoir plus.
        </Typography>
      </Box>
    </Box>
  );
}
